import * as log from 'loglevel';
import Request from '../request';

import ResourceLoader from '../resource-loader';
// Lazy loaded: import Fuse from 'fuse.js'

import {render} from 'lit-html';
import Translate from '../translate';

import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';
import {classMap} from 'lit-html/directives/class-map';
import {styleMap} from 'lit-html/directives/style-map';
import {ifDefined} from 'lit-html/directives/if-defined';
import {unsafeHTML} from 'lit-html/directives/unsafe-html';

import TemplateRegistry from '../template/template-registry';
import {
    sizeClassMap, buttonStyleMap, buttonColorStyleMap,
    optionClassMap, optionName, optionValueText, imgTooltipValueText
} from '../template/template-util.js';
import {ThemeCurrency} from '../const';
import {optionDebounce} from '../template/template-util.js';
import {cdnImg,cdnSize} from '../shopify-cdn';


class LibraryController {

    constructor(onChange, getOption) {
        this.onChange = onChange;
        this.getOption = getOption;
        this.config = null; // reference updated on each render
        this.option = null; // reference updated on each render

        // global settings
        this.settings = LibraryController.settings;

        // in cloud we use assets url to cache option lists (better performance for large lists)   SAME LOGIC: app-options.js
        this.settings.api = this.settings.api.indexOf('local') === -1 ? this.settings.api.replace('api.','assets.'): this.settings.api;
    }

    init() {

        // all matching images
        this.images = [];
        // currently shown images
        this.imagesPaged = [];
        this.imagesHide = true;
        this.page = 1;

        // cache for fetched lists
        this.cache = {};

        // search engine reference
        this.searchQuery = '';
        this.fuse = null;
        this.searchLists = true;

        // selected category
        this.category = null;
        this.categoryDefault = null; // pre selected category

        // check if external image is set via url or javascript
        this.checkExternalImage();
    }

    initUI(){
        this.categoryCount = this.option.values?.length || 0;

        // only in editor mode this function is called again (make sure to exclude the selected image)
        if(this.image){
            this.categoryCount--;
        }

        this.searchHide = this.categoryCount === 0; // hide search if no categories are available

        this.pageSize = this.option.params.librarySize || 20;

        // auto select category if only one category is available
        if(!this.category && this.categoryCount > 0) {
            // single category mark as default (no need to show category selection)
            if(this.categoryCount === 1 && !this.config.editorMode){ // editor mode disable default category selection
                this.categoryDefault = this.option.values[0];

                this.onCategoryChange(null, this.categoryDefault);
            }
            // more than one category - only auto select if not grid
            else if(this.option.params.categoryGrid !== true){
                this.onCategoryChange(null, this.option.values[0]);
            }
        }

        // check if upload-lift option is connected
        if(this.option.params.optionUpload){
            // get option reference via helper method (we lookup on every change - allows to switch fields in editorMode)
            let option = this.getOption(this.option.params.optionUpload);

            // flag to register event listener only once per option
            this._uploadEvents = option !== this.optionUpload;

            this.optionUpload = option;

            // true or false - allows toggle setting in editorMode
            if(this.option.params.uploadHide !== undefined){
                // wait for rendering to be finished then trigger show hide of upload-lift
                this.config.productForm.addEventListener('options:rendered', e => {
                    let optionUploadEl = document.querySelector(`[data-option="${this.option.params.optionUpload}"]`);
                    if(optionUploadEl){
                        // toggle show/hide
                        optionUploadEl.style.display = this.option.params.uploadHide ? 'none' : '';
                    }
                }, { once: true });
            }
        }

    }

    pageImages() {
        this.imagesPaged = [];
        this.page = 0;
        this.imagesHide = false;
        this.categoryHide = this.option.params.categoryGrid === true || this.categoryDefault !== null; // hide category if shown as grid OR only single default category
        this.onMoreImages();
    }

    onImageChange(e, image) {

        // remove previous image from values
        this.removeImage();

        // support price taken from current category INFO: currently not used as the UI of the categories does not show the price input
        if(this.category && this.category.price && !image.price && image.price !== 0){
            image.price = this.category.price;
        }

        this.image = image;

        // add to values (categories) to support validation & custom price calculation
        this.option.values.push(image);

        let imgUrl = image.img;

        // external image custom url support
        if(typeof window.clLibraryGetImage === 'function'){
            imgUrl = window.clLibraryGetImage(image);
        }

        // save selected image url as value (same as upload option)
        this.option.value = imgUrl;

        // close popup if open
        if(this.popupOpen){
            this._popup.set('close', true);
        }

        // check if upload-lift option is connected
        if(this.optionUpload){
            this.syncUploadLiftImage(imgUrl);
        }

        // trigger change
        this.onChange(this.option);
    }

    removeImage(){
        // remove previous image from values
        if(this.image){
            // find index in this.option.values
            let index = this.option.values.indexOf(this.image);
            if(index !== -1){
                // remove in array
                this.option.values.splice(index, 1);
            }

            this.image = null;
        }

        // reset img url
        this.option.value = '';
    }

    restoreImage(){
        // just set image again
        let image = {
            id: this.option._imageId, // optional image id (restored in product-options.js)
            img: this._corsUrl(this.option.value) // CORS requires as the url could have been cropped and uploaded to our servers
        };

        // select image
        this.onImageChange(null, image);
    }

    _corsUrl(url){
        // CORS fix
        url = url.replace('https://upload.dev.cloudlift.app/s/dev', 'https://storage.googleapis.com/cloudlift-app-cloud-dev-upload');
        url = url.replace('https://upload.cloudlift.app/s', 'https://storage.googleapis.com/cloudlift-app-cloud-prod-upload');
        return url;
    }

    isSelected(option, optionValue) {
        // Reference check (single-select: this.image is the selected value)
        if (this.image === optionValue) return true;
        // Value check (multi-select instances: deep copy breaks reference, compare by URL)
        if (optionValue.img === option.value) return true;
        return false;
    }

    // custom function to return category value (same as shown on backend UI when conditions are setup)
    conditionValue(option){
        return this.category?.value || '';
    }

    onValidate(option)  {

        // custom validation - INFO: default ID validation not working because we hide the image input in the template
        if(option.required && !this.image){
            option.errors.push(Translate.get('product.options.validity.valueMissing'));
            return false;
        }

        return true;
    }

    showCategories() {
        this.categoryHide = false;
        this.imagesHide = true;

        this.renderOptions();
    }

    async onCategoryChange(e, category) {
        log.debug('library category changed', e);

        // store selected category
        this.category = category;

        // reset search
        this.searchQuery = '';

        this.images = await this.getImages(this.category);

        // pagination and render
        this.pageImages();
    }

    async onMoreImages(){

        this.page++;

        let start = (this.page - 1) * this.pageSize;
        let end = start + this.pageSize;

        this.hasMoreImages = end < this.images.length;

        // external images support
        if(this.searchQuery){

            if(typeof window.clLibrarySearchImages === 'function'){
                await this.callExternalImages(window.clLibrarySearchImages, end, this.searchQuery, this.page, this.pageSize);
            }

        } else if(typeof window.clLibraryGetImages === 'function'){
            await this.callExternalImages(window.clLibraryGetImages, end, this.category, this.page, this.pageSize);
        }

        // default images pagination logic
        this.imagesPaged.push(... this.images.slice(start, end));

        // render search results
        this.renderOptions();
    }

    async callExternalImages(fn, end, ...args){

        try {

            log.debug('call external images using function:', fn)

            let result = await fn.apply(this, args);

            // add results if found
            if(result?.images?.length){
                // add page to images
                this.imagesPaged.push(...result?.images);

                if(result.total){
                    this.hasMoreImages = result.total > end;
                }
            }
        } catch (error) {
            log.debug('error loading images using function', error);
        }
    }

    checkExternalImage(){

        let externalImage;

        if(window.location.search.indexOf('library.') !== -1){

            let params = new URLSearchParams(window.location.search);

            externalImage = {};

            // map all library.* params to image object (allows to set custom properties)
            params.forEach((value, key) => {
                if(key.startsWith('library.')){
                    externalImage[key.replace('library.', '')] = value;
                }
            });
        }
        // image set in theme via javascript
        else if(typeof window.clLibraryImage === 'object'){
            externalImage = window.clLibraryImage;
        }
        // NEW: image set in theme via javascript (multiple images key by option name)
        else if(typeof window.clLibraryImages === 'object' && window.clLibraryImages[this.option.name]){
            externalImage = window.clLibraryImages[this.option.name];
        }

        // img url is required
        if(externalImage?.img){
            // add full link instead of protocol relative (shopify metafields)
            if(externalImage.img.startsWith('//')){
                externalImage.img = 'https:' + externalImage.img;
            }

            // IMPORTANT: we have to use a timeout to avoid breaking other options that are initialized (e.g dimension option x/y input was broken)
            setTimeout(() => {
                this.onImageChange(null, externalImage);
            });
        }

    }

    async onSearch(e) {
        log.debug('library search changed', e);

        this.searchQuery = e.target.value;

        if(!this.fuse && this.searchLists){

            let allImages = await this.fetchAllOptionsLists();

            if(allImages.length){

                // async load fuse.js from cdn (only once)
                await ResourceLoader.loadOnce(['https://cdn.jsdelivr.net/npm/fuse.js@6.6.2/dist/fuse.basic.min.js'], 'fuse-search',  { async: false });

                // https://fusejs.io/api/options.html
                const options = {
                    threshold: 0.4, // lower the more exact
                    ignoreLocation: true,  // don't prefer location of the match for ranking
                    includeScore: true,
                    keys: [
                        {
                            name: 'text', // text search higher weight
                            weight: 2
                        },
                        {
                            name: 'value',
                            weight: 1
                        }]
                };

                this.fuse = new Fuse(allImages, options);

            } else {
                // no images from list found > disable search locally
                this.searchLists = false;
            }
        }

        if(this.searchLists){
            let results = this.fuse.search(this.searchQuery);

            console.debug('search results:', results);

            // map results to option values
            this.images = results.map(function(result) { return result.item });

        } else {
            this.images = [];
        }

        // search empty & default category set  (single category logic to always stay within category)
        if(this.searchQuery === '' && this.categoryDefault) {
            this.onCategoryChange(null, this.categoryDefault);
        } else {
            this.category = null;
            this.pageImages();
        }
    }

    async getImages(category) {

        let images = [];

        // check if list is linked
        if(category.list){
            images = await this.fetchOptionList(category.list);
        }

        return images;
    }

    async fetchAllOptionsLists() {
        let fetchPromises = [];

        for (const optionCategory of this.option.values) {
            // check if list is linked
            if(optionCategory.list){
                // fetch without await to start all requests at once
                fetchPromises.push(this.fetchOptionList(optionCategory.list));
            }
        }

        // wait for all requests to finish
        return Promise.all(fetchPromises).then((lists) => {
           // flatten all lists into single array
           return lists.flat();
        });
    }

    async fetchOptionList(identifier) {

        // return cached result
        if(this.cache[identifier]) {
            return this.cache[identifier];
        }

        let response = await new Request(this.settings.api + this.settings.path + '/' + identifier, {
            method: 'GET',
            responseType: 'json',
            headers: {
                'Content-type': 'application/json'
            },
            queryParams: this.settings.queryParams
        });

        let images = response.data;

        // store in cache
        this.cache[identifier] = images;

        return images;
    }

    libraryStyleMap(styleMap = {}){

        // hide if popup is enabled and not open
        if(this.option.params.popup && !this.popupOpen){
            styleMap.display = 'none';
        }

        if(this.option.params.libraryCols){
            styleMap['--library-grid-cols'] = this.option.params.libraryCols;
        }
        if(this.option.params.libraryColsM){
            styleMap['--library-grid-cols-m'] = this.option.params.libraryColsM;
        }
        if(this.option.params.libraryColsH){
            styleMap['--library-grid-cols-h'] = this.option.params.libraryColsH + 'px';
        }

        return styleMap;
    }

    async openPopup() {

        // only init once - then toggle
        if(!this._popupEl){

            // create wrapper to render into
            this._popupEl = document.createElement('div');

            // SAME as defined in personalize logic
            let resources = [
                this.settings.assets + '/static/lib/ldcover/ldcv.full.min.css',
                this.settings.assets + '/static/lib/ldcover/ldcv.min.js'
            ];
            await ResourceLoader.loadOnce(resources, 'popup',  {async: false });

            // find own option element library wrapper
            const selector = `[data-option="${this.option.name}"] .cl-po--library`;
            // first check in wrapper || fallback to full document (required for personalize popup support)
            this._libraryEl = this.config.wrapper.querySelector(selector) || document.querySelector(selector);

            // initial render
            this.renderPopup();

            document.body.appendChild(this._popupEl);

            this._popup = new ldCover({ root: this._popupEl.querySelector('.ldcv'), lock: false }); // pass the reference to the popup root

            this._popup.on('toggle.on', () => {

                this.popupOpen = true;

                // make visible (render resets display to none)
                this._libraryEl.style.display = '';

                // if we want to render again (currently not needed as we manually set the display style to '' above)
                //this.renderOptions();

            });
        } else {
            // render again to update library contents
            this.renderPopup();
        }

        // popup returns promise when closed
        this._popup.get().then((result) => {
            this.popupOpen = false;
        });

        this._popup.toggle();
    }

    renderOptions(){
        this.config.productForm.dispatchEvent(new CustomEvent('options:render', { detail: this.option, cancelable: true, bubbles: true }));
    }

    renderPopup(){

        const config = {
            id: this.config.uuid,
            className: 'popup fade' + (this.option.params.popupFull ? ' full' : ''),
        }

        render(TemplateRegistry.templates['library-popup'](this._libraryEl, config, (e) => {
            this._popup.set('close', true);
        }), this._popupEl);
    }

    crop()  {

        // upload-lift file pond item reference
        if(this._fileCrop){
            // set flag to avoid remove logic
            this._syncUpload = true;
            this._syncCrop = true;
            // save current image
            this.image._img = this.image._preview;
            // set loading preview again
            this.image._preview = this._preview;

            // Dimension crop support > apply new crop ratios > open image editor
            if(this.optionUpload._controller?._cropConfig){
                this.optionUpload._controller.updateCrop();
            } else {
                // opens image editor
                this._fileCrop.edit();
            }


        }
        // default Live crop on preview
        else {
            this.config.productForm.dispatchEvent(new CustomEvent('option:crop', { detail: this.option, cancelable: true, bubbles: true }));
        }

    }

    // preview image as base64 svg animated spinner image
    _preview = 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/Pgo8IURPQ1RZUEUgc3ZnIFBVQkxJQyAiLS8vVzNDLy9EVEQgU1ZHIDEuMS8vRU4iICJodHRwOi8vd3d3LnczLm9yZy9HcmFwaGljcy9TVkcvMS4xL0RURC9zdmcxMS5kdGQiPgo8c3ZnIHdpZHRoPSI0MHB4IiBoZWlnaHQ9IjQwcHgiIHZpZXdCb3g9IjAgMCA0MCA0MCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4bWw6c3BhY2U9InByZXNlcnZlIiBzdHlsZT0iZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7c3Ryb2tlLWxpbmVqb2luOnJvdW5kO3N0cm9rZS1taXRlcmxpbWl0OjEuNDE0MjE7IiB4PSIwcHgiIHk9IjBweCI+CiAgICA8ZGVmcz4KICAgICAgICA8c3R5bGUgdHlwZT0idGV4dC9jc3MiPjwhW0NEQVRBWwogICAgICAgICAgICBALXdlYmtpdC1rZXlmcmFtZXMgc3BpbiB7CiAgICAgICAgICAgICAgZnJvbSB7CiAgICAgICAgICAgICAgICAtd2Via2l0LXRyYW5zZm9ybTogcm90YXRlKDBkZWcpCiAgICAgICAgICAgICAgfQogICAgICAgICAgICAgIHRvIHsKICAgICAgICAgICAgICAgIC13ZWJraXQtdHJhbnNmb3JtOiByb3RhdGUoLTM1OWRlZykKICAgICAgICAgICAgICB9CiAgICAgICAgICAgIH0KICAgICAgICAgICAgQGtleWZyYW1lcyBzcGluIHsKICAgICAgICAgICAgICBmcm9tIHsKICAgICAgICAgICAgICAgIHRyYW5zZm9ybTogcm90YXRlKDBkZWcpCiAgICAgICAgICAgICAgfQogICAgICAgICAgICAgIHRvIHsKICAgICAgICAgICAgICAgIHRyYW5zZm9ybTogcm90YXRlKC0zNTlkZWcpCiAgICAgICAgICAgICAgfQogICAgICAgICAgICB9CiAgICAgICAgICAgIHN2ZyB7CiAgICAgICAgICAgICAgICAtd2Via2l0LXRyYW5zZm9ybS1vcmlnaW46IDUwJSA1MCU7CiAgICAgICAgICAgICAgICAtd2Via2l0LWFuaW1hdGlvbjogc3BpbiAxLjVzIGxpbmVhciBpbmZpbml0ZTsKICAgICAgICAgICAgICAgIC13ZWJraXQtYmFja2ZhY2UtdmlzaWJpbGl0eTogaGlkZGVuOwogICAgICAgICAgICAgICAgYW5pbWF0aW9uOiBzcGluIDEuNXMgbGluZWFyIGluZmluaXRlOwogICAgICAgICAgICB9CiAgICAgICAgXV0+PC9zdHlsZT4KICAgIDwvZGVmcz4KICAgIDxnIGlkPSJvdXRlciI+CiAgICAgICAgPGc+CiAgICAgICAgICAgIDxwYXRoIGQ9Ik0yMCwwQzIyLjIwNTgsMCAyMy45OTM5LDEuNzg4MTMgMjMuOTkzOSwzLjk5MzlDMjMuOTkzOSw2LjE5OTY4IDIyLjIwNTgsNy45ODc4MSAyMCw3Ljk4NzgxQzE3Ljc5NDIsNy45ODc4MSAxNi4wMDYxLDYuMTk5NjggMTYuMDA2MSwzLjk5MzlDMTYuMDA2MSwxLjc4ODEzIDE3Ljc5NDIsMCAyMCwwWiIgc3R5bGU9ImZpbGw6YmxhY2s7Ii8+CiAgICAgICAgPC9nPgogICAgICAgIDxnPgogICAgICAgICAgICA8cGF0aCBkPSJNNS44NTc4Niw1Ljg1Nzg2QzcuNDE3NTgsNC4yOTgxNSA5Ljk0NjM4LDQuMjk4MTUgMTEuNTA2MSw1Ljg1Nzg2QzEzLjA2NTgsNy40MTc1OCAxMy4wNjU4LDkuOTQ2MzggMTEuNTA2MSwxMS41MDYxQzkuOTQ2MzgsMTMuMDY1OCA3LjQxNzU4LDEzLjA2NTggNS44NTc4NiwxMS41MDYxQzQuMjk4MTUsOS45NDYzOCA0LjI5ODE1LDcuNDE3NTggNS44NTc4Niw1Ljg1Nzg2WiIgc3R5bGU9ImZpbGw6cmdiKDIxMCwyMTAsMjEwKTsiLz4KICAgICAgICA8L2c+CiAgICAgICAgPGc+CiAgICAgICAgICAgIDxwYXRoIGQ9Ik0yMCwzMi4wMTIyQzIyLjIwNTgsMzIuMDEyMiAyMy45OTM5LDMzLjgwMDMgMjMuOTkzOSwzNi4wMDYxQzIzLjk5MzksMzguMjExOSAyMi4yMDU4LDQwIDIwLDQwQzE3Ljc5NDIsNDAgMTYuMDA2MSwzOC4yMTE5IDE2LjAwNjEsMzYuMDA2MUMxNi4wMDYxLDMzLjgwMDMgMTcuNzk0MiwzMi4wMTIyIDIwLDMyLjAxMjJaIiBzdHlsZT0iZmlsbDpyZ2IoMTMwLDEzMCwxMzApOyIvPgogICAgICAgIDwvZz4KICAgICAgICA8Zz4KICAgICAgICAgICAgPHBhdGggZD0iTTI4LjQ5MzksMjguNDkzOUMzMC4wNTM2LDI2LjkzNDIgMzIuNTgyNCwyNi45MzQyIDM0LjE0MjEsMjguNDkzOUMzNS43MDE5LDMwLjA1MzYgMzUuNzAxOSwzMi41ODI0IDM0LjE0MjEsMzQuMTQyMUMzMi41ODI0LDM1LjcwMTkgMzAuMDUzNiwzNS43MDE5IDI4LjQ5MzksMzQuMTQyMUMyNi45MzQyLDMyLjU4MjQgMjYuOTM0MiwzMC4wNTM2IDI4LjQ5MzksMjguNDkzOVoiIHN0eWxlPSJmaWxsOnJnYigxMDEsMTAxLDEwMSk7Ii8+CiAgICAgICAgPC9nPgogICAgICAgIDxnPgogICAgICAgICAgICA8cGF0aCBkPSJNMy45OTM5LDE2LjAwNjFDNi4xOTk2OCwxNi4wMDYxIDcuOTg3ODEsMTcuNzk0MiA3Ljk4NzgxLDIwQzcuOTg3ODEsMjIuMjA1OCA2LjE5OTY4LDIzLjk5MzkgMy45OTM5LDIzLjk5MzlDMS43ODgxMywyMy45OTM5IDAsMjIuMjA1OCAwLDIwQzAsMTcuNzk0MiAxLjc4ODEzLDE2LjAwNjEgMy45OTM5LDE2LjAwNjFaIiBzdHlsZT0iZmlsbDpyZ2IoMTg3LDE4NywxODcpOyIvPgogICAgICAgIDwvZz4KICAgICAgICA8Zz4KICAgICAgICAgICAgPHBhdGggZD0iTTUuODU3ODYsMjguNDkzOUM3LjQxNzU4LDI2LjkzNDIgOS45NDYzOCwyNi45MzQyIDExLjUwNjEsMjguNDkzOUMxMy4wNjU4LDMwLjA1MzYgMTMuMDY1OCwzMi41ODI0IDExLjUwNjEsMzQuMTQyMUM5Ljk0NjM4LDM1LjcwMTkgNy40MTc1OCwzNS43MDE5IDUuODU3ODYsMzQuMTQyMUM0LjI5ODE1LDMyLjU4MjQgNC4yOTgxNSwzMC4wNTM2IDUuODU3ODYsMjguNDkzOVoiIHN0eWxlPSJmaWxsOnJnYigxNjQsMTY0LDE2NCk7Ii8+CiAgICAgICAgPC9nPgogICAgICAgIDxnPgogICAgICAgICAgICA8cGF0aCBkPSJNMzYuMDA2MSwxNi4wMDYxQzM4LjIxMTksMTYuMDA2MSA0MCwxNy43OTQyIDQwLDIwQzQwLDIyLjIwNTggMzguMjExOSwyMy45OTM5IDM2LjAwNjEsMjMuOTkzOUMzMy44MDAzLDIzLjk5MzkgMzIuMDEyMiwyMi4yMDU4IDMyLjAxMjIsMjBDMzIuMDEyMiwxNy43OTQyIDMzLjgwMDMsMTYuMDA2MSAzNi4wMDYxLDE2LjAwNjFaIiBzdHlsZT0iZmlsbDpyZ2IoNzQsNzQsNzQpOyIvPgogICAgICAgIDwvZz4KICAgICAgICA8Zz4KICAgICAgICAgICAgPHBhdGggZD0iTTI4LjQ5MzksNS44NTc4NkMzMC4wNTM2LDQuMjk4MTUgMzIuNTgyNCw0LjI5ODE1IDM0LjE0MjEsNS44NTc4NkMzNS43MDE5LDcuNDE3NTggMzUuNzAxOSw5Ljk0NjM4IDM0LjE0MjEsMTEuNTA2MUMzMi41ODI0LDEzLjA2NTggMzAuMDUzNiwxMy4wNjU4IDI4LjQ5MzksMTEuNTA2MUMyNi45MzQyLDkuOTQ2MzggMjYuOTM0Miw3LjQxNzU4IDI4LjQ5MzksNS44NTc4NloiIHN0eWxlPSJmaWxsOnJnYig1MCw1MCw1MCk7Ii8+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4K';

    syncUploadLiftImage(imgUrl) {

        if(this.optionUpload._controller?.field){

            let field = this.optionUpload._controller.field;

            log.debug('library image upload-lift sync', field, imgUrl);

            // set loading preview
            this.image._preview = this._preview;

            // only register listeners once
            if(this._uploadEvents){

                delete this._uploadEvents; // set as registered

                // IMPORTANT: we have to listen to added event (crop button will re-add a new file)
                this.optionUpload._controller.field.element.addEventListener('upload:added', (e) => {

                    // only if we are uploading the file from the library (upload could be manually made on the upload-lift option)
                    if(this._syncUpload && this.image){

                        let fileMeta = e.detail;

                        // we only handle original image (live preview also only loads original - thumbnail event is triggered after original so it does not make sense to then update the img with the thumbnail)
                        if(fileMeta.meta.variant === 'original'){

                            // update image preview
                            this.image._preview = fileMeta.url;

                            // return most recent filepond level file item (used to crop image again)
                            let filePondFile = field._upload._pond.getFile();

                            // edit method added in   FilePondPluginImageEditor.esm.js > item.extend('edit', () => {});
                            if(filePondFile && filePondFile.edit){
                                // store reference (shows crop button)
                                this._fileCrop = filePondFile;
                            }

                            // hacky way to detect crop ratio updates (from Upload-Lift)
                            if(!this._updateCropOriginal){
                                this._updateCropOriginal = this.optionUpload._controller.updateCrop;
                                this.optionUpload._controller.updateCrop = () => {
                                    // set flag to avoid remove logic
                                    this._syncUpload = true;
                                    this._syncCrop = true;
                                    this._updateCropOriginal();
                                }
                            }


                            // trigger change (render UI)
                            this.onChange(this.option);

                            // reset upload flag
                            delete this._syncUpload;
                            delete this._syncCrop;
                        }
                    }


                });

                // listen to remove and remove image
                this.optionUpload._controller.field.element.addEventListener('upload:removed', (e) => {

                    // avoid remove when we are uploading a new file
                    if(!this._syncUpload && this.image){

                        this.removeImage();

                        delete this._fileCrop;

                        // trigger change (render UI)
                        this.onChange(this.option);
                    }

                });

                // listen to editor cancel event
                this.optionUpload._controller.field.element.addEventListener('upload:editor:cancel', (e) => {

                    if(this._syncUpload && this.image){

                        // restore previous preview
                        if(this.image._img){
                            this.image._preview = this.image._img;
                            delete this.image._img;
                        }

                        // after new image selection > closed > remove image
                        // if _syncCrop is set - the editor can be open/closed without the need for removing the file (keep previous selected crop)
                        if(!this._syncCrop){
                            this.removeImage();
                        }

                        // trigger change (render UI)
                        this.onChange(this.option);

                        // reset upload flag
                        delete this._syncUpload;
                        delete this._syncCrop;
                    }

                });
            }

            // flag that we are currently upload/edit a file
            this._syncUpload = true;
            this._syncCrop = false;

            const uploadImgUrl = () => {
                // upload url directly to field  INFO: this removes previously uploaded images automatically
                this.optionUpload._controller.field.uploadFile(imgUrl).then((fileMeta) => {
                    log.debug('library image upload-lift complete', fileMeta);
                });
            }

            // IMPORTANT: wait for full file upload (filepond) ready (mainly required when image is set via external url on page load)
            if(this.optionUpload._controller.field._upload?.isReady()){
                uploadImgUrl();
            } else {
                this.optionUpload._controller.field.element.addEventListener('upload:ready', () => uploadImgUrl(), { once: true });
            }

        }
    }
}

// global settings (set in product-options.js)
LibraryController.settings = {
    assets: '',
    api: '',
    path: '/api/list',
    queryParams: {},
}

export default LibraryController;

TemplateRegistry.templates['library'] = (option, config, onChange, onErrorClear, getOption) => {

    let init = option._controller === undefined;

    // get or create layer customer controller
    option._controller = option._controller || new LibraryController(onChange, getOption);

    // option reference can change (mainly in editor mode) so we just always update it to the current reference
    option._controller.option = option;
    // config is always a new object - storing reference on controller init would keep the old instance
    option._controller.config = config;

    if(init){
        option._controller.init();
    }
    // value set but no image > try to restore image (back from cart)
    else if(option.value && !option._controller.image){
        // needs to be an URL (old value can be set from option type changes)
        if(option.value.indexOf('http') !== -1){
            option._controller.restoreImage();
        }
    }
    // editor mode always update call to show updated ui
    if(init || config.editorMode){
        option._controller.initUI();
    }

    return html`

          ${option.params.popup ? // library in popup
                html`<div class="cl-po--swatch-container" data-library-popup>
                         <label class="cl-po--swatch-item grow" >
                                <div class="cl-po--swatch cl-po--swatch-button ${classMap(sizeClassMap(option, 'small'))}" style=${styleMap(buttonStyleMap(option,buttonColorStyleMap(option)))}
                                     @click=${(e) => option._controller.openPopup() }>
                                     ${Translate.get('product.options.library.button', null, 'Select image')}
                                </div>
                         </label>
                    </div>`
                : null }


          ${option._controller.image ? // currently selected image - property saved on cart (img element hidden when not in a popup AND not crop or editor is enabled)
                  html`<div class="cl-po--swatch-container" data-library-image>

                      ${option._controller.image.id ? // store image id on cart if defined (external image)
                              html`<input type="hidden" id="${option.id}-id" name="properties[_${option.name}_id]" value=${option._controller.image.id} form=${ifDefined(config.productFormLink)}>` : null}
                       
                       <label class="cl-po--swatch-item ${classMap({ 'cl-hide': !option.params.popup && !(option._layer?.params.crop || option._layer?.params.layerEditor || option._controller.optionUpload) })}">
                            <input type="radio" class="cl-po--input ${classMap(optionClassMap(option))}" id="${option.id}" name="${optionName(option)}" .value=${option.value}  
                               checked ?required=${option.required} form=${ifDefined(config.productFormLink)}>
                            <div class="cl-po--swatch cl-po--swatch-img  ${classMap(sizeClassMap(option, 'large'))}" data-tippy-content="${ifDefined(imgTooltipValueText(option, option._controller.image, ThemeCurrency.moneyFormat))}" data-tippy-placement="bottom">
                                 ${option._controller.image.img ? html`<img src="${option._controller.image._preview ? option._controller.image._preview : cdnImg(option._controller.image.img, cdnSize(option.params.size || 'large'))}" alt="${optionValueText(option, option._controller.image, ThemeCurrency.moneyFormat)}"/>` : null}
                            </div>
                      </label>

                      ${option._layer?.params.crop || option._controller._fileCrop ? // Live Crop with upload layer connected to library OR upload-lift file reference INFO: cropLabel only configurable via live preview layer
                           html`
                          <div class="cl-po--swatch-item cl-po--upload">
                            <div class="cl-po--input cl-po--upload-button crop ${classMap({'has-label': (option._layer || option._controller.optionUpload?._layer)?.params.cropLabel})}" @click=${e => option._controller.crop()}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M15 17v2H6a1 1 0 0 1-1-1V7H2V5h3V2h2v15h8zm2 5V7H9V5h9a1 1 0 0 1 1 1v11h3v2h-3v3h-2z" /></svg>
                                ${(option._layer || option._controller.optionUpload?._layer)?.params.cropLabel ?  html`<span class="cl-po--crop-label">${(option._layer || option._controller.optionUpload?._layer)?.params.cropLabel}</span>` : null}
                            </div>
                          </div>
                        `: null}
                      
                      ${option._layer?.params.layerEditor ?  html`${TemplateRegistry.templates['layer_editor'](option, config, onChange)}`: null}
                  </div>` : null }
          
            <div class="cl-po--library" style=${styleMap(option._controller.libraryStyleMap())}">
                
                <div class="cl-po--library-search ${classMap({ 'cl-hide': option._controller.searchHide })}">
                     <input type="text" class="cl-po--input" id="${option.id}_search" name="${option.name}_search" .value=${option._controller.searchQuery}
                            placeholder=${Translate.get('product.options.library.search.placeholder', null, 'Search images')}
                            @input=${optionDebounce(option, (e) => option._controller.onSearch(e))} >
                </div>
                <div class="cl-po--library-categories ${classMap({ 'cl-hide': option._controller.categoryHide })}">

                    ${option._controller.categoryCount > 0 ?  html`<div class="cl-po--label">${Translate.get('product.options.library.categories.label', null, 'Categories')}</div>`: null}
                    
                    ${option.params.categoryGrid ? // show categories as grid 
                        html`
                        <div class="cl-po--library-grid">
                             ${repeat(option.values || [], (categoryValue) => categoryValue.value,  (categoryValue,index) => html`
                                 ${option._controller.image !== categoryValue ? // EXCLUDE the selected image (added in the the option to allow selection and price add-ons)
                                     html`
                                     <div class="cl-po--library-grid-item">
                                         <div class="cl-po--swatch ${classMap({ 'cl-checked': option._controller.category === categoryValue })}" 
                                              @click=${(e) => option._controller.onCategoryChange(e, categoryValue)}>
                                             ${categoryValue.img ? html`<img src="${cdnImg(categoryValue.img, cdnSize('large'))}" alt="${optionValueText(option, categoryValue, ThemeCurrency.moneyFormat)}"/>` : null}
                                             <span class="cl-po--library-category-text">${unsafeHTML(optionValueText(option, categoryValue, ThemeCurrency.moneyFormat))}</span>
                                         </div>
                                     </div>` : null }
                            `)}
                        </div>
                        ` // Show categories as swatches (keeps the categories visible when selecting a category)
                        : html`
                        <div class="cl-po--swatch-container">
                            ${repeat(option.values || [], (categoryValue) => categoryValue.value, (categoryValue,index) => html`
                                ${option._controller.image !== categoryValue ? // EXCLUDE the selected image (added in the the option to allow selection and price add-ons)
                                    html`
                                    <div class="cl-po--swatch-item" >
                                        <div class="cl-po--swatch cl-po--swatch-button ${classMap(sizeClassMap(option, 'small', { 'cl-checked': option._controller.category === categoryValue }))}"
                                             @click=${(e) => option._controller.onCategoryChange(e, categoryValue)}>
                                            ${unsafeHTML(optionValueText(option, categoryValue, ThemeCurrency.moneyFormat))}
                                        </div>
                                    </div>` : null }
                            `)}
                        </div>`}
                </div>
                ${!option._controller.imagesHide && option._controller.categoryHide ? // Category title & back button
                        html`<div class="cl-po--library-images-category" >

                                <div class="cl-po--swatch cl-po--swatch-button cl-po--swatch-button-library ${classMap({ 'cl-hide': option._controller.categoryCount <= 1 })}" @click=${(e) => option._controller.showCategories() }>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2ZM12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20ZM12 11H16V13H12V16L8 12L12 8V11Z"></path></svg>
                                    ${Translate.get('product.options.library.categories.button', null, 'Show categories')}
                                </div>
                            
                             </div>`
                        : null }
                <div class="cl-po--library-images ${classMap({ 'cl-hide': option._controller.imagesHide })}">
                    
                    <div class="cl-po--library-grid">
                        ${repeat(option._controller.imagesPaged || [], (imageValue) => imageValue.value,  (imageValue,index) => html`
                        <div class="cl-po--library-grid-item">
                            <div class="cl-po--swatch ${classMap({ 'cl-checked': option._controller.image === imageValue })}" data-tippy-content="${ifDefined(imgTooltipValueText(option, imageValue, ThemeCurrency.moneyFormat))}" data-tippy-placement="bottom"
                                 @click=${(e) => option._controller.onImageChange(e, imageValue)}>
                                ${imageValue.img ? html`<img src="${cdnImg(imageValue.img, cdnSize('large'))}" alt="${optionValueText(option, imageValue, ThemeCurrency.moneyFormat)}"/>` : null}
                            </div>
                        </div>
                        `)}
                    </div>

                    ${option._controller.searchQuery && option._controller.imagesPaged.length === 0 ? // no results
                            html`
                                <div class="cl-po--library-images-more">
                                    <p>${Translate.get('product.options.library.search.empty', null, 'No results found')}</p>
                                </div>
                            ` : null }

                    ${option._controller.hasMoreImages ? // show more button
                            html`
                                <div class="cl-po--library-images-more">
                                    <div class="cl-po--swatch cl-po--swatch-button cl-po--swatch-button-library" @click=${(e) => option._controller.onMoreImages() }>
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2ZM12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20ZM13 12H16L12 16L8 12H11V8H13V12Z"></path></svg>
                                        ${Translate.get('product.options.library.images.button', null, 'More images')}
                                    </div>
                                </div>
                               ` : null }
                    
                </div>
            </div>`;
};

TemplateRegistry.templates['library-popup'] = (libraryEl, config, onClose) => {

    return html`
        <div class="ldcv ${config.className}" >
            <div class="base">
                <div class="inner card">
                    <div class="cl-po--popup cl-po--popup-library">
                        <div class="cl-po--popup-options cl--po-wrapper" id=${config.id}>
                            ${libraryEl}
                        </div>
                    </div>
                    <div class="cl-po--popup-close" @click="${(e) => onClose(e)}">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ><path fill="none" d="M0 0h24v24H0z"/><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm0-9.414l2.828-2.829 1.415 1.415L13.414 12l2.829 2.828-1.415 1.415L12 13.414l-2.828 2.829-1.415-1.415L10.586 12 7.757 9.172l1.415-1.415L12 10.586z"/></svg>
                    </div>
                </div>
            </div>
        </div>
    `
};